// Claims & variations summary (V0.4.3): money by approval step (tiles + pipeline), a "paid?" / "with client too long"
// banner, work progress of client-approved variations, and money by building. Every number filters the list.
// Switches are remembered per person, per browser (same prefs as the RFI summary).
import { Link } from 'react-router';
import { WORK, type Progress, type Step, type WorkKey } from '../lib/progress';

/** amount in the register's basis (claims VIP, variations VEP); alt = the same amount in the other basis */
export type MoneyRow = { id: string; amount: number | null; alt?: number | null; building_ids: string[]; p: Progress };
export type MoneyFilter = { f: string; work: string; b: string };
type Prefs = { resp: boolean; bldg: boolean; hidden: boolean };

const M = (n: number) => `${n < 0 ? '−' : ''}${(Math.abs(n) / 1e6).toFixed(2)}M`;
const amt = (r: MoneyRow) => Number(r.amount ?? 0);
const sum = (rs: MoneyRow[]) => rs.reduce((s, r) => s + amt(r), 0);
const altSum = (rs: MoneyRow[]) => rs.reduce((s, r) => s + Number(r.alt ?? 0), 0);
const plural = (n: number, one: string, many = one + 's') => `${n} ${n === 1 ? one : many}`;

const BAR: Record<Step, string> = { review: '#A3470D', hold: '#F2C29C', swi: '#A9D3A4', client: '#2E6B3A', paid: '#DDE3DD', complete: '#DDE3DD', rejected: '#E4DACD' };

export function MoneySummary({
  type,
  rows,
  buildings,
  filter,
  setFilter,
  prefs,
  setPrefs,
  paymentsLink,
}: {
  type: 'claim' | 'variation';
  rows: MoneyRow[];
  buildings: { id: string; name: string }[];
  filter: MoneyFilter;
  setFilter: (k: keyof MoneyFilter, v: string) => void;
  prefs: Prefs;
  setPrefs: (p: Partial<Prefs>) => void;
  paymentsLink: boolean;
}) {
  if (prefs.hidden) return null;
  const claim = type === 'claim';
  const by = (s: Step) => rows.filter((r) => r.p.step === s);
  const review = by('review');
  const swi = by('swi');
  const client = by('client');
  const done = claim ? by('paid') : by('complete');
  const hold = by('hold');
  const rej = by('rejected');
  const stale = rows.filter((r) => r.p.stale);
  const toggle = (k: keyof MoneyFilter, v: string) => setFilter(k, filter[k] === v ? '' : v);
  const basis = claim ? 'VIP' : 'VEP';
  const other = claim ? 'VEP' : 'VIP';
  const tile = (key: string, cls: string, label: string, money: string, sub: string, attn = false, rs: MoneyRow[] = []) => (
    <button type="button" className={`mk ${cls} ${attn ? 'attn' : ''} ${filter.f === key ? 'on' : ''}`} aria-pressed={filter.f === key} onClick={() => toggle('f', key)}>
      <span className="lbl">
        {attn ? <span className="pdot" aria-hidden="true" /> : null}
        {label}
      </span>
      <b>
        {money} <small className="bas">{basis}</small>
      </b>
      <span className="alt">
        {other} {M(altSum(rs))}
      </span>
      <span className="sub">{sub}</span>
    </button>
  );
  const oldest = review.reduce((m, r) => Math.max(m, r.p.days), 0);
  const swiPlus = swi.filter((r) => amt(r) > 0);
  const swiMinus = swi.filter((r) => amt(r) < 0);
  const fullDone = client.filter((r) => r.p.work === 'full').length;
  const certN = swi.filter((r) => r.p.cert).length;

  // money by building
  const bName = new Map(buildings.map((b) => [b.id, b.name]));
  const cols: Step[] = claim ? ['review', 'swi', 'client', 'paid'] : ['review', 'swi', 'client', 'complete', 'rejected'];
  const bmap = new Map<string, { id: string; name: string; v: Record<string, number>; total: number }>();
  for (const r of rows) {
    if (r.p.step === 'hold' || (claim && r.p.step === 'rejected')) continue;
    const ids = r.building_ids.length ? r.building_ids : ['none'];
    for (const id of ids) {
      if (!bmap.has(id)) bmap.set(id, { id, name: id === 'none' ? 'No building' : (bName.get(id) ?? 'Unknown'), v: {}, total: 0 });
      const e = bmap.get(id)!;
      e.v[r.p.step] = (e.v[r.p.step] ?? 0) + amt(r) / ids.length;
      if (r.p.step !== 'rejected') e.total += amt(r) / ids.length;
    }
  }
  const blist = [...bmap.values()].sort((a, b) => (a.id === 'none' ? 1 : b.id === 'none' ? -1 : Math.abs(b.total) - Math.abs(a.total)));
  const top = blist.slice(0, 8);
  const rest = blist.slice(8);
  const cell = (v: number | undefined) => (v && Math.abs(v) >= 5 ? (v / 1e6).toFixed(2) : '—');
  const pipe = (claim ? (['review', 'hold', 'swi', 'client', 'paid'] as Step[]) : (['review', 'hold', 'swi', 'client', 'complete'] as Step[])).map((s) => ({
    s,
    v: by(s).reduce((t, r) => t + Math.max(0, amt(r)), 0),
  }));
  const pipeTotal = pipe.reduce((t, x) => t + x.v, 0);
  const PIPE_LABEL: Record<string, string> = claim
    ? { review: 'SWI review', hold: 'Hold', swi: 'SWI approved · with finance', client: 'Client approved', paid: 'Paid' }
    : { review: 'SWI review', hold: 'Hold', swi: 'SWI approved · with client', client: 'Client approved', complete: 'Complete' };

  const work = WORK.map((w) => ({ ...w, rs: client.filter((r) => r.p.work === w.key) }));
  const workUnset = client.filter((r) => !r.p.work);

  return (
    <section className="msum" aria-label="Summary">
      <div className="msum-kpis">
        {tile('review', 'review', 'With SWI to review', `FJD ${M(sum(review))}`, review.length ? `${plural(review.length, claim ? 'claim' : 'variation')} · oldest ${oldest} d` : 'nothing waiting', review.length > 0, review)}
        {claim
          ? tile('swi', 'swi', '✓ SWI approved · with finance', `FJD ${M(sum(swi))}`, `${plural(swi.length, 'claim')}${certN ? ` · ${certN} for cert / certified` : ''}`, false, swi)
          : tile('swi', 'swi', '✓ SWI approved · with client', `${sum(swi) < 0 ? '−' : ''}FJD ${M(Math.abs(sum(swi)))}${swiMinus.length ? ' net' : ''}`, `${swi.length}${swiMinus.length ? ` · +${M(sum(swiPlus))} adds / ${M(sum(swiMinus))} omissions` : ''}`, false, swi)}
        {tile('client', 'client', claim ? '✓ Client approved · to pay' : '✓ Client approved', `FJD ${M(sum(client))}`, claim ? plural(client.length, 'claim') : `${client.length} · ${fullDone} fully done`, false, client)}
        {tile(claim ? 'paid' : 'complete', 'done', claim ? '✓ Paid' : '✓ Complete', `FJD ${M(sum(done))}`, plural(done.length, claim ? 'claim' : 'variation'), false, done)}
        {claim
          ? tile('hold', 'hold', 'On hold', `FJD ${M(sum(hold))}`, plural(hold.length, 'claim'), false, hold)
          : tile('other', 'other', 'Hold · rejected', `${M(sum(hold))} · ${M(sum(rej))}`, `${hold.length} on hold · ${rej.length} rejected`, false, [...hold, ...rej])}
      </div>

      {stale.length ? (
        <div className={`msum-banner ${claim ? 'paidq' : 'waiting'}`}>
          <span className="tagx">{claim ? 'Paid?' : 'With client'}</span>
          <span className="txt">
            {claim ? (
              <>
                <b>
                  {plural(stale.length, 'approved claim')} (FJD {M(sum(stale))}) {stale.length === 1 ? 'is' : 'are'} over 90 days old with no payment recorded.
                </b>{' '}
                Payment instructions from Summation Partners are matched to claims in the{' '}
                {paymentsLink ? <Link to="/payments">payment log</Link> : 'payment log'} — exact amounts are one click to assign.
              </>
            ) : (
              <>
                <b>
                  {plural(stale.length, 'SWI-approved variation')} {stale.length === 1 ? 'has' : 'have'} waited over 90 days for client approval
                </b>
                {stale.filter((r) => amt(r) < 0).length ? ` (${stale.filter((r) => amt(r) < 0).length} of them omissions)` : ''}. Chase finance, or mark them client approved if the client already signed.
              </>
            )}
          </span>
          <button type="button" className="btn btn-secondary" onClick={() => toggle('f', 'stale')} aria-pressed={filter.f === 'stale'}>
            {filter.f === 'stale' ? 'Show all' : 'Show them'}
          </button>
        </div>
      ) : null}

      <div className="rsum-show">
        <span>Show</span>
        <button type="button" className={`tg ${prefs.resp ? 'on' : ''}`} aria-pressed={prefs.resp} onClick={() => setPrefs({ resp: !prefs.resp })}>
          <span className="knob" aria-hidden="true" />
          {claim ? 'Money pipeline' : 'Work progress'}
        </button>
        <button type="button" className={`tg ${prefs.bldg ? 'on' : ''}`} aria-pressed={prefs.bldg} onClick={() => setPrefs({ bldg: !prefs.bldg })}>
          <span className="knob" aria-hidden="true" />
          By building
        </button>
      </div>

      {prefs.resp && claim && pipeTotal > 0 ? (
        <div className="msum-pipe">
          <div className="rr-head">
            <span className="lbl">Money pipeline · all claims FJD {M(pipeTotal)} VIP · {M(altSum(rows.filter((r) => amt(r) > 0)))} VEP</span>
            <span className="muted small">Click a block to filter</span>
          </div>
          <div className="mp-bar" role="group" aria-label="Claim money by step">
            {pipe.map((x) =>
              x.v > 0 ? (
                <button
                  key={x.s}
                  type="button"
                  className={`mp-seg s-${x.s} ${filter.f === x.s ? 'on' : ''}`}
                  style={{ flex: x.v }}
                  aria-label={`${PIPE_LABEL[x.s]} FJD ${M(x.v)}`}
                  title={`${PIPE_LABEL[x.s]} — FJD ${M(x.v)}`}
                  onClick={() => toggle('f', x.s)}
                >
                  {x.v / pipeTotal > 0.1 ? `${PIPE_LABEL[x.s]} ${M(x.v)}` : ''}
                </button>
              ) : null,
            )}
          </div>
        </div>
      ) : null}

      {prefs.resp && !claim ? (
        <div className="msum-pipe">
          <div className="rr-head">
            <span className="lbl">Work progress · {plural(client.length, 'client-approved variation')}</span>
            <span className="muted small">count · FJD M VEP</span>
          </div>
          <div className="wp-grid">
            {work.map((w) => (
              <button key={w.key} type="button" className={`wp ${filter.work === w.key ? 'on' : ''}`} aria-pressed={filter.work === w.key} onClick={() => toggle('work', w.key)}>
                <Meter q={w.q} />
                <span className="wp-l">
                  {w.label} · <b>{w.rs.length}</b>
                </span>
                <span className="sub">
                  {M(sum(w.rs))}
                  {w.key === 'full' && w.rs.length ? ' · PM ticks Complete' : ''}
                </span>
              </button>
            ))}
            <button type="button" className={`wp ${filter.work === 'unset' ? 'on' : ''}`} aria-pressed={filter.work === 'unset'} onClick={() => toggle('work', 'unset')}>
              <Meter q={null} />
              <span className="wp-l">
                Not set · <b>{workUnset.length}</b>
              </span>
              <span className="sub">{M(sum(workUnset))}</span>
            </button>
          </div>
        </div>
      ) : null}

      {prefs.bldg ? (
        <div className="msum-bldg">
          <span className="lbl">Money by building · FJD M {claim ? 'VIP' : 'VEP'}</span>
          <div className="mb-wrap">
            <table className="mb">
              <thead>
                <tr>
                  <th>Building</th>
                  {cols.map((c) => (
                    <th key={c} className={`right c-${c}`}>
                      {c === 'review' ? 'With SWI' : c === 'swi' ? 'SWI approved' : c === 'client' ? 'Client approved' : c === 'paid' ? 'Paid' : c === 'complete' ? 'Complete' : 'Rejected'}
                    </th>
                  ))}
                  <th className="right">Total</th>
                  <th className="mb-split">Split</th>
                </tr>
              </thead>
              <tbody>
                {top.map((b) => (
                  <tr key={b.id} className={filter.b === b.id ? 'on' : ''}>
                    <td>
                      <button type="button" className="linkish" onClick={() => toggle('b', b.id)} aria-pressed={filter.b === b.id}>
                        {b.name}
                      </button>
                    </td>
                    {cols.map((c) => (
                      <td key={c} className="right">
                        {cell(b.v[c])}
                      </td>
                    ))}
                    <td className="right">
                      <b>{(b.total / 1e6).toFixed(2)}</b>
                    </td>
                    <td className="mb-split">
                      <span className="mb-bar">
                        {cols
                          .filter((c) => c !== 'rejected' && (b.v[c] ?? 0) > 0)
                          .map((c) => (
                            <span key={c} style={{ flex: b.v[c], background: BAR[c] }} />
                          ))}
                      </span>
                    </td>
                  </tr>
                ))}
                {rest.length ? (
                  <tr>
                    <td className="muted">{plural(rest.length, 'more building')}</td>
                    {cols.map((c) => (
                      <td key={c} className="right muted">
                        {cell(rest.reduce((t, b) => t + (b.v[c] ?? 0), 0))}
                      </td>
                    ))}
                    <td className="right muted">{(rest.reduce((t, b) => t + b.total, 0) / 1e6).toFixed(2)}</td>
                    <td className="mb-split" />
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/** ¼ ½ ¾ Full in four small squares; null = not set (dashed). */
export function Meter({ q, faded }: { q: number | null; faded?: boolean }) {
  return (
    <span className={`meter ${q === null ? 'unset' : ''} ${faded ? 'faded' : ''}`} aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className={q !== null && i < q ? 'on' : ''} />
      ))}
    </span>
  );
}

/** The 4-part progress strip under the step chip. */
/** Four-segment strip (V0.4.9): reached steps green, the next step amber and pulsing (on hold: amber, still). */
export function Strip({ step }: { step: Step }) {
  const at = STRIP_AT[step];
  const ended = step === 'paid' || step === 'complete';
  return (
    <span className={`pstrip ${ended ? 'ended' : ''}`} aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className={step === 'rejected' ? 'off' : i <= at ? 'ok' : i === at + 1 ? (step === 'hold' ? 'held' : 'nx') : 'off'} />
      ))}
    </span>
  );
}
const STRIP_AT: Record<Step, number> = { review: 0, hold: 0, swi: 1, client: 2, paid: 3, complete: 3, rejected: -1 };

export const WORK_LABEL = (w: WorkKey | null) => (w ? (WORK.find((x) => x.key === w)?.label ?? '') : 'not set');
